import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NgClass } from '@angular/common';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { BYTES_IN_KB, FileDescription } from 'components/file-upload/file-upload.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { ServiceFormActionsComponent } from 'components/service/service-form-actions/service-form-actions.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { IrFormLabelsService } from 'services/ir-form-labels.service';
import { ToastService } from 'services/toast.service';
import { ClientCard } from 'types/client.interface';
import { LegalEntityCard, LegalEntityFormOption } from 'types/legal-entity.interface';
import {
  CLIENT_PAYER_FILE_TYPE_BY_KEY,
  FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
  FILE_OBJECT_TYPE_INFO_REQUEST_DEATH_CERTIFICATE,
  FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT,
  FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT,
  InformationRequestCard,
  InformationRequestTopicResponse
} from 'types/information-request.interface';
import { FILE_TERM_TYPE_UNKNOWN, FileCard } from 'types/file.interface';
import { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';
import { formatDatetime, formatDate } from 'util/date.utility';
import { countFileActionFailures, firstFileActionError, isolateFileActions } from 'util/file-upload.utility';

const BOOLEAN_FIELD_TYPES = new Set(['radio_button', 'radio', 'checkbox', 'toggle']);

@Component({
  selector: 'app-info-request-summary',
  standalone: true,
  imports: [TranslateModule, NgClass, NgbCollapseModule, ConfirmModalComponent, ServiceFormActionsComponent, TooltipComponent],
  templateUrl: './info-request-summary.component.html',
  styleUrls: ['../../../form/base-form.scss', './info-request-summary.component.scss']
})
export class InfoRequestSummaryComponent implements OnInit {
  public readonly formatDatetime = formatDatetime;
  public readonly formatDate = formatDate;
  @Input() client!: ClientCard;
  @Input() application!: InformationRequestCard;
  @Input() topics: InformationRequestTopicResponse[] = [];
  @Input() documentFiles: { files: File[]; descriptions: FileDescription[] } = { files: [], descriptions: [] };
  @Input() clientPayerFiles: Record<string, { files: File[]; descriptions: FileDescription[] }> = {};
  /** Queued files a step save has already uploaded; "Iesniegt" does not send them again. */
  @Input() uploadedFiles: ReadonlySet<File> = new Set<File>();
  @Input() legalEntityInput?: LegalEntityCard;
  @Input() jpCompanyData: Record<string, string> = {};
  @Output() applicationUpdated = new EventEmitter<InformationRequestCard>();
  @Output() documentFilesChanged = new EventEmitter<{ files: File[]; descriptions: FileDescription[] }>();
  @Output() clientPayerFilesChanged = new EventEmitter<Record<string, { files: File[]; descriptions: FileDescription[] }>>();
  @Output() submitted = new EventEmitter<void>();

  isRequestExpanded: boolean = true;
  isClientPayerExpanded: boolean = true;
  isConfirmSubmit: boolean = false;
  isConfirmDelete: boolean = false;
  isSubmitting: boolean = false;
  structuralUnits: StructuralUnitMinimalListItem[] = [];
  legalEntityFormOptions: LegalEntityFormOption[] = [];

  ngOnInit(): void {
    this.labels.ensureLoaded();
    this.apiService.getLegalEntityForms().subscribe({
      next: (options) => { this.legalEntityFormOptions = options; },
      error: (err) => console.error(err)
    });
  }

  get isOrganization(): boolean {
    return this.client?.clientType === 'Organization' || !!this.application?.legalEntity;
  }

  get isDraft(): boolean {
    return this.application?.backofficeStatus === 'Draft';
  }

  get topicName(): string {
    const topic = this.topics.find(t => t.topicType === this.application?.topicType);
    return topic?.name || '-';
  }

  get parsedTopicFieldData(): { [key: string]: any } {
    if (!this.application?.topicFieldData) return {};
    try {
      return JSON.parse(this.application.topicFieldData);
    } catch {
      return {};
    }
  }

  get parsedRequesterData(): Record<string, string> | null {
    if (!this.application?.requesterForOtherPersonData) return null;
    try {
      return JSON.parse(this.application.requesterForOtherPersonData);
    } catch {
      return null;
    }
  }

  get parsedDiscountData(): Record<string, string> | null {
    if (!this.application?.discountGroupData) return null;
    try {
      return JSON.parse(this.application.discountGroupData);
    } catch {
      return null;
    }
  }

  get parsedPayerData(): Record<string, string> | null {
    if (!this.application?.payerData) return null;
    try {
      return JSON.parse(this.application.payerData);
    } catch {
      return null;
    }
  }

  get requesterForOtherPersonType(): string | undefined {
    return this.application?.requesterForOtherPersonType;
  }

  get requesterRelationLabel(): string {
    const prefix = 'pages.service.infoRequest.clientPayer.';
    const type = this.application?.requesterRelationType;
    switch (type) {
      case 'ForSelf': return this.translateService.instant(prefix + 'forSelf');
      case 'ForOtherPerson': return this.getForOtherPersonLabel();
      case 'OtherCase': return this.translateService.instant(prefix + 'otherCaseGeneral');
      default: return '-';
    }
  }

  private getForOtherPersonLabel(): string {
    const prefix = 'pages.service.infoRequest.clientPayer.';
    const base = this.translateService.instant(prefix + 'forOtherPerson');
    switch (this.requesterForOtherPersonType) {
      case 'AuthorizationOrPermission':
        return `${base} ${this.translateService.instant(prefix + 'authorizationOrPermission').toLowerCase()}`;
      case 'DeceasedRelative':
        return `${base} - ${this.translateService.instant(prefix + 'deceasedRelative').toLowerCase()}`;
      case 'Other':
        return `${base} - ${this.translateService.instant(prefix + 'otherCase').toLowerCase()}`;
      default:
        return base;
    }
  }

  get discountCardTitle(): string {
    const prefix = 'pages.service.infoRequest.clientPayer.';
    switch (this.application?.discountGroupType) {
      case 'FirstOrSecondGroupDisability': return this.translateService.instant(prefix + 'discountDisability');
      case 'LowIncomePerson': return this.translateService.instant(prefix + 'discountLowIncome');
      case 'PoliticallyRepressed': return this.translateService.instant(prefix + 'discountRepressed');
      case 'ChildProtection': return this.translateService.instant(prefix + 'discountChildProtection');
      case 'OtherCase': return this.translateService.instant(prefix + 'discountOther');
      default: return '';
    }
  }

  private getTableSection(selectedTopic: InformationRequestTopicResponse): {
    title: string;
    fieldKeys: string[];
    fieldDefs: { fieldKey: string; fieldLabel: string }[];
  } | null {
    const sectionTitles = selectedTopic.fields
      .filter(f => f.fieldType === 'section_title')
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (sectionTitles.length <= 1) return null;
    const secondTitle = sectionTitles[1];
    const tableDefs = selectedTopic.fields
      .filter(f => f.sortOrder > secondTitle.sortOrder && f.fieldType !== 'section_title' && f.blockIndex === 0)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (tableDefs.length === 0) return null;
    return {
      title: secondTitle.sectionTitle || secondTitle.fieldLabel || '',
      fieldKeys: tableDefs.map(f => f.fieldKey),
      fieldDefs: tableDefs.map(f => ({ fieldKey: f.fieldKey, fieldLabel: f.fieldLabel }))
    };
  }

  private buildBlockTable(
    data: Record<string, any>,
    tableSection: { fieldDefs: { fieldKey: string; fieldLabel: string }[] },
    blockNumber: number
  ): { columns: { label: string; key: string }[]; rows: Record<string, string>[] } {
    const arrayFields: { key: string; label: string; values: string[] }[] = [];

    for (const def of tableSection.fieldDefs) {
      const dataKey = blockNumber === 1 ? def.fieldKey : `${def.fieldKey}_block${blockNumber}`;
      const value = data[dataKey];
      if (typeof value !== 'string' || !value.startsWith('[')) continue;
      try {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed)) continue;
        arrayFields.push({ key: def.fieldKey, label: def.fieldLabel, values: parsed.map(String) });
      } catch { /* not JSON */ }
    }

    if (arrayFields.length === 0) return { columns: [], rows: [] };

    const columns = arrayFields.map(f => ({ label: f.label, key: f.key }));
    const maxRows = Math.max(...arrayFields.map(f => f.values.length));
    const rows: Record<string, string>[] = [];

    for (let i = 0; i < maxRows; i++) {
      const row: Record<string, string> = {};
      for (const field of arrayFields) {
        row[field.key] = field.values[i] ?? '';
      }
      rows.push(row);
    }

    return { columns, rows };
  }

  get topicBlocks(): {
    blockNumber: number;
    title: string;
    fields: { key: string; label: string; value: string }[];
    tableSectionTitle: string;
    tableColumns: { label: string; key: string }[];
    tableRows: Record<string, string>[];
  }[] {
    const selectedTopic = this.topics.find(t => t.topicType === this.application?.topicType);
    if (!selectedTopic) return [];
    const data = this.parsedTopicFieldData;
    const blockCount = this.application?.blockCount || 1;
    const tableSection = this.getTableSection(selectedTopic);
    const tableFieldKeySet = new Set(tableSection?.fieldKeys || []);

    const sortedFields = [...selectedTopic.fields]
      .filter(f => f.fieldType !== 'section_title' && !tableFieldKeySet.has(f.fieldKey))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    // Mirror form logic: when maxBlocks > 1 and all fields have blockIndex === 0,
    // treat ALL fields as block template fields
    const hasMultipleBlocks = selectedTopic.maxBlocks > 1;
    const allFieldsAreGlobal = selectedTopic.fields.every(f => f.blockIndex === 0);

    let blockTemplateFields: typeof sortedFields;
    let nonBlockFields: typeof sortedFields;

    if (allFieldsAreGlobal) {
      blockTemplateFields = sortedFields;
      nonBlockFields = [];
    } else {
      blockTemplateFields = sortedFields.filter(f => f.blockIndex > 0);
      nonBlockFields = sortedFields.filter(f => f.blockIndex === 0);
    }

    const blockLabel = blockCount > 1 ? (selectedTopic.blockLabel ?? selectedTopic.name) : '';

    const blocks: {
      blockNumber: number; title: string;
      fields: { key: string; label: string; value: string }[];
      tableSectionTitle: string;
      tableColumns: { label: string; key: string }[];
      tableRows: Record<string, string>[];
    }[] = [];

    for (let b = 1; b <= blockCount; b++) {
      const fields: { key: string; label: string; value: string }[] = [];

      if (b === 1) {
        nonBlockFields.forEach(f => {
          fields.push({ key: f.fieldKey, label: f.fieldLabel, value: this.resolveTopicFieldValue(data[f.fieldKey], f.fieldType) });
        });
      }

      blockTemplateFields.forEach(f => {
        const key = b === 1 ? f.fieldKey : `${f.fieldKey}_block${b}`;
        fields.push({ key, label: f.fieldLabel, value: this.resolveTopicFieldValue(data[key], f.fieldType) });
      });

      const table = tableSection ? this.buildBlockTable(data, tableSection, b) : { columns: [], rows: [] };

      blocks.push({
        blockNumber: b,
        title: blockLabel ? `${blockLabel} Nr. ${b}` : '',
        fields,
        tableSectionTitle: tableSection?.title || '',
        tableColumns: table.columns,
        tableRows: table.rows
      });
    }

    return blocks;
  }

  private resolveTopicFieldValue(value: any, fieldType?: string): string {
    if (value === '' || value === null || value === undefined) return '-';
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      try {
        const arr = JSON.parse(value);
        if (Array.isArray(arr)) {
          const filtered = arr.map(v => String(v ?? '').trim()).filter(v => v.length > 0);
          return filtered.length ? filtered.join(', ') : '-';
        }
      } catch { /* not JSON, fall through */ }
    }
    return this.formatTopicFieldValue(value, fieldType);
  }

  private formatTopicFieldValue(value: any, fieldType?: string): string {
    if (BOOLEAN_FIELD_TYPES.has(fieldType ?? '')) {
      if (value === true || value === 'true') return 'Jā';
      if (value === false || value === 'false') return 'Nē';
    }
    return String(value);
  }

  getLegalFormDisplay(): string {
    const raw = this.jpCompanyData['legalForm'] || this.application.legalEntity?.type || this.legalEntityInput?.type;
    if (!raw) return '-';
    return this.getLegalEntityFormName(raw);
  }

  getLegalEntityFormName(code: string): string {
    if (!code) return '-';
    return this.legalEntityFormOptions.find(o => o.code === code)?.name || code;
  }

  getCompanyNameDisplay(): string {
    return this.application.legalEntity?.name || this.legalEntityInput?.name || '-';
  }

  getRegistrationNumberDisplay(): string {
    return this.application.legalEntity?.registrationNumber || this.legalEntityInput?.registrationNumber || '-';
  }

  getRegistrationCountryDisplay(): string {
    return this.application.legalEntity?.countryName || this.legalEntityInput?.country?.name || '-';
  }

  getLegalAddressDisplay(): string {
    return this.jpCompanyData['legalAddress'] || this.application.legalEntity?.legalAddress || this.legalEntityInput?.legalAddress || '-';
  }

  getCompanyPhoneDisplay(): string {
    return this.jpCompanyData['companyPhone'] || this.application.legalEntity?.phone || '-';
  }

  getCompanyEmailDisplay(): string {
    return this.jpCompanyData['companyEmail'] || this.application.legalEntity?.email || '-';
  }

  getContactPersonEmailDisplay(): string {
    return this.jpCompanyData['contactPersonEmail'] || this.application.contactPersonEmail || '-';
  }

  getContactPersonPhoneDisplay(): string {
    return this.jpCompanyData['contactPersonPhone'] || this.application.contactPersonPhone || '-';
  }

  constructor(
    private router: Router,
    private apiService: ApiService,
    private toastService: ToastService,
    private translateService: TranslateService,
    public labels: IrFormLabelsService
  ) {
    this.apiService.getStructuralUnitOptions().subscribe({
      next: (data) => { this.structuralUnits = data; },
      error: (err) => console.error(err)
    });
  }

  getStructuralUnitName(id?: string | null): string {
    if (!id) return '-';
    return this.structuralUnits.find(u => u.id === id)?.name || '-';
  }

  toggleRequestSection(): void {
    this.isRequestExpanded = !this.isRequestExpanded;
  }

  toggleClientPayerSection(): void {
    this.isClientPayerExpanded = !this.isClientPayerExpanded;
  }

  editStep(step: number): void {
    this.router.navigate([], {
      queryParams: { solis: step },
      queryParamsHandling: 'merge'
    });
  }

  goBack(): void {
    this.router.navigate([], {
      queryParams: { solis: 2 },
      queryParamsHandling: 'merge'
    });
  }

  onDelete(): void {
    this.isConfirmDelete = true;
  }

  confirmDelete(): void {
    this.isConfirmDelete = false;
    this.apiService.deleteInformationRequest(this.application.id).subscribe({
      next: () => {
        this.toastService.showSuccess('applicationDraftDeleted', '', { shouldTranslate: true });
        this.router.navigate(['/informacijas-pieprasijums']);
      },
      error: () => {
        this.toastService.showRequestError();
      }
    });
  }

  onSubmit(): void {
    this.confirmSubmit();
  }

  confirmSubmit(): void {
    this.isSubmitting = true;
    this.isConfirmSubmit = false;

    // LNA-1850. This page lists the files still queued on steps 1 and 2, but the steps are rendered
    // with @if, so the upload boxes that queued them are gone and nothing else would send them — a file
    // carried forward through the step bar was shown as attached and never reached the request. Send
    // them first. A refused upload stops the submit, and the toast names the reason (for the ten-file
    // cap, the cap).
    const uploads$ = this.queuedFileUploads();
    const uploaded$ = uploads$.length === 0 ? of([]) : forkJoin(isolateFileActions(uploads$));
    uploaded$.subscribe((results) => {
      if (countFileActionFailures(results) > 0) {
        this.isSubmitting = false;
        this.toastService.showRequestError(firstFileActionError(results));
        return;
      }

      this.submitRequest();
    });
  }

  /**
   * Exactly the files this page lists as not yet on the request, less the ones a step save has already
   * sent. Re-sending a sent one is not safe: the server's duplicate check skips deleted files, so one
   * the operator has since removed would come back, and its old note would overwrite an edited one.
   */
  private queuedFileUploads(): Observable<unknown>[] {
    const upload = (files: File[], descriptions: FileDescription[], fileType: string): Observable<unknown>[] =>
      files
        .filter((file) => !this.uploadedFiles.has(file))
        .map((file) =>
          this.apiService.uploadFile(
            file,
            fileType,
            this.application.id,
            descriptions.find((d) => d.fileName === file.name && !d.outerId)?.description ?? '',
            FILE_TERM_TYPE_UNKNOWN
          )
        );

    return [
      ...upload(this.pendingDocumentFiles, this.documentFiles.descriptions, FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT),
      ...Object.entries(CLIENT_PAYER_FILE_TYPE_BY_KEY).flatMap(([key, fileType]) =>
        upload(this.getPendingClientPayerFiles(key), this.clientPayerFiles[key]?.descriptions ?? [], fileType)
      )
    ];
  }

  private submitRequest(): void {
    this.apiService.submitInformationRequest(this.application.id).subscribe({
      next: (data) => {
        this.isSubmitting = false;
        this.applicationUpdated.emit(data);
        this.toastService.showSuccess('infoRequestSubmitted', '', { shouldTranslate: true });
        this.submitted.emit();
        this.router.navigate(['/informacijas-pieprasijums', this.application.id]);
      },
      error: (error) => {
        console.error(error);
        this.isSubmitting = false;
        // LNA-2022 (LNAEP-1072). The submit endpoint refuses conditional-mandatory blocks the
        // backoffice form does not mark required, and every refusal used to surface as the bare
        // "Sistēmas kļūda" toast — the error was not even passed on. Route it through the shared
        // resolver so the operator is told which block to fill in.
        this.toastService.showValidationOrRequestError(error);
      }
    });
  }

  getPayerTypeLabel(): string {
    const type = this.application.payerType;
    if (!type) return '-';
    const prefix = 'pages.service.infoRequest.clientPayer.';
    const map: { [key: string]: string } = {
      Requester: 'payerRequester',
      OtherPhysicalPerson: 'payerOtherPerson',
      OtherLegalEntity: 'payerLegalEntity',
      FreeOfCharge: 'payerFreeOfCharge'
    };
    return this.translateService.instant(`${prefix}${map[type] || type}`);
  }

  getDeadlineLabel(): string {
    const type = this.application.serviceDeadline;
    if (!type) return '-';
    const prefix = 'pages.service.infoRequest.clientPayer.';
    const map: { [key: string]: string } = {
      Days30: 'days30',
      Days15: 'days15',
      Days10: 'days10',
      Other: 'deadlineOther'
    };
    return this.translateService.instant(`${prefix}${map[type] || type}`);
  }

  getDiscountLabel(): string {
    const type = this.application.discountGroupType;
    if (!type) return '-';
    const prefix = 'pages.service.infoRequest.clientPayer.';
    const map: { [key: string]: string } = {
      None: 'discountNone',
      FirstOrSecondGroupDisability: 'discountDisability',
      LowIncomePerson: 'discountLowIncome',
      PoliticallyRepressed: 'discountRepressed',
      ChildProtection: 'discountChildProtection',
      OtherCase: 'discountOther'
    };
    return this.translateService.instant(`${prefix}${map[type] || type}`);
  }

  getInvoiceDeliveryLabel(): string {
    const method = this.application.invoiceDeliveryMethod;
    if (!method) return '-';
    // The e-address captions are not under `clientPayer` but one level up, one per block, so the keys
    // here are relative to `infoRequest`. Pointing at a missing key printed it raw on the summary
    // (LNA-1850).
    const prefix = 'pages.service.infoRequest.';
    const map: { [key: string]: string } = {
      Portal: 'clientPayer.portal',
      Email: 'clientPayer.emailMethod',
      EAddress: 'invoiceEAddress',
      PostalMail: 'clientPayer.postalMail',
      Archive: 'clientPayer.invoiceArchive',
      FreeOfCharge: 'clientPayer.invoiceFreeOfCharge'
    };
    return this.translateService.instant(`${prefix}${map[method] || method}`);
  }

  getResponseDeliveryLabel(): string {
    const method = this.application.responseDeliveryMethod;
    if (!method) return '-';
    // Relative to `infoRequest`, as in getInvoiceDeliveryLabel.
    const prefix = 'pages.service.infoRequest.';
    const map: { [key: string]: string } = {
      Portal: 'clientPayer.portal',
      Email: 'clientPayer.emailMethod',
      EAddress: 'responseEAddress',
      PostalMail: 'clientPayer.postalMail',
      Archive: 'clientPayer.responseArchive'
    };
    return this.translateService.instant(`${prefix}${map[method] || method}`);
  }

  getPurposeLabel(): string {
    const purpose = this.application.purpose;
    if (!purpose) return '-';
    const prefix = 'pages.service.infoRequest.clientPayer.';
    const map: { [key: string]: string } = {
      PrivateUse: 'purposePrivate',
      SubmissionToInstitution: 'purposeInstitution',
      SubmissionToForeignInstitution: 'purposeForeign'
    };
    return this.translateService.instant(`${prefix}${map[purpose] || purpose}`);
  }

  getSubmissionMethodLabel(): string {
    const method = this.application.submissionChannel;
    if (!method) return '-';
    const map: { [key: string]: string } = {
      Email: 'submissionEmail',
      Namejs: 'submissionNamejs',
      Archive: 'submissionArchive',
      PostalMail: 'submissionPostalMail'
    };
    return this.translateService.instant(`pages.service.infoRequest.${map[method] || method}`);
  }

  get existingDocumentFiles(): FileCard[] {
    return (this.application?.documents || []).filter(
      d => d.objectType === FILE_OBJECT_TYPE_INFO_REQUEST_DOCUMENT
    );
  }

  get pendingDocumentFiles(): File[] {
    const serverNames = new Set(this.existingDocumentFiles.map(f => f.uploadFileName));
    return this.documentFiles.files.filter(f => !serverNames.has(f.name));
  }

  hasDocumentFiles(): boolean {
    return this.pendingDocumentFiles.length > 0 || this.existingDocumentFiles.length > 0;
  }

  downloadServerFile(file: FileCard): void {
    this.apiService.downloadFile(file.outerId!).subscribe((data) => {
      const blob = new Blob([data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.uploadFileName!;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }

  private readonly clientPayerFileTypeMap: Record<string, string> = {
    jpAuthorization: FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
    authorization: FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
    deathCertificate: FILE_OBJECT_TYPE_INFO_REQUEST_DEATH_CERTIFICATE,
    discountGroup: FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT
  };

  getExistingClientPayerFiles(key: string): FileCard[] {
    const type = this.clientPayerFileTypeMap[key];
    if (!type) return [];

    // Authorization and deathCertificate share the same objectType string,
    // so we must filter by current requesterForOtherPersonType to prevent leaking
    if (key === 'authorization') {
      if (this.isOrganization) {
        if (!this.parsedRequesterData) return [];
      } else {
        if (this.requesterForOtherPersonType !== 'AuthorizationOrPermission') return [];
      }
    }
    if (key === 'deathCertificate') {
      if (this.requesterForOtherPersonType !== 'DeceasedRelative') return [];
    }

    return (this.application?.documents || []).filter(d => (d.objectType as string) === type);
  }

  getPendingClientPayerFiles(key: string): File[] {
    const serverNames = new Set(this.getExistingClientPayerFiles(key).map(f => f.uploadFileName));
    return (this.clientPayerFiles[key]?.files || []).filter(f => !serverNames.has(f.name));
  }

  hasClientPayerFiles(key: string): boolean {
    return this.getPendingClientPayerFiles(key).length > 0 || this.getExistingClientPayerFiles(key).length > 0;
  }

  getClientPayerServerFileDescription(file: FileCard, key: string): string {
    const fromDescriptions = this.findServerFileDescription(file, this.clientPayerFiles[key]?.descriptions);
    if (fromDescriptions !== undefined && fromDescriptions !== '') return fromDescriptions;
    return file.fileNote?.note || '';
  }

  getFileDescription(fileName: string, descriptions: FileDescription[]): string {
    return descriptions.find((d) => d.fileName === fileName)?.description || '';
  }

  getServerFileDescription(file: FileCard): string {
    const fromDescriptions = this.findServerFileDescription(file, this.documentFiles.descriptions);
    if (fromDescriptions !== undefined && fromDescriptions !== '') return fromDescriptions;
    return file.fileNote?.note || '';
  }

  // An uploaded file is identified by its outerId, not its name: the backend allows two files with the
  // same name in one request (scans arrive as IMG_0001.pdf), and keying on the name gave them each
  // other's comments. Same rule as file-upload.component.ts — a description restored from the wizard's
  // saved state carries no id until the server card arrives, so the name is the fallback for those.
  private findServerFileDescription(file: FileCard, descriptions: FileDescription[] | undefined): string | undefined {
    const byId = descriptions?.find(d => !!file.outerId && d.outerId === file.outerId);
    if (byId) return byId.description;
    return descriptions?.find(d => !d.outerId && d.fileName === file.uploadFileName)?.description;
  }

  formatFileSize(size: number): string {
    if (size < BYTES_IN_KB) {
      return `${Math.ceil(size)} B`;
    } else if (size < BYTES_IN_KB * BYTES_IN_KB) {
      return `${Math.ceil(size / BYTES_IN_KB)} KB`;
    }
    return `${Math.ceil(size / BYTES_IN_KB / BYTES_IN_KB)} MB`;
  }

  removeServerDocumentFile(file: FileCard): void {
    this.apiService.deleteFile(file.outerId!).subscribe({
      next: () => {
        this.application = {
          ...this.application,
          documents: (this.application.documents || []).filter(d => d.outerId !== file.outerId)
        };
        this.applicationUpdated.emit(this.application);
        this.toastService.showSuccess('fileDeleteSuccess', '', { shouldTranslate: true });
      },
      error: () => {
        this.toastService.showRequestError();
      }
    });
  }

  removeDocumentFile(file: File): void {
    this.documentFiles = {
      files: this.documentFiles.files.filter(f => f !== file),
      descriptions: this.documentFiles.descriptions.filter(d => d.fileName !== file.name)
    };
    this.documentFilesChanged.emit(this.documentFiles);
  }

  removeClientPayerFile(key: string, file: File): void {
    const entry = this.clientPayerFiles[key];
    if (!entry) return;
    this.clientPayerFiles = {
      ...this.clientPayerFiles,
      [key]: {
        files: entry.files.filter(f => f !== file),
        descriptions: entry.descriptions.filter(d => d.fileName !== file.name)
      }
    };
    this.clientPayerFilesChanged.emit(this.clientPayerFiles);
  }

  downloadLocalFile(file: File): void {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
